import React, { useEffect, useState } from 'react';
import Seo from '@/components/seo';
import { Button, Card, Col, Container, Form, Row, Table, } from 'react-bootstrap';
import Turnstile from 'react-turnstile';
import { InputMask } from 'primereact/inputmask';
import { format, http, layout, messages, validate, } from '@/helpers/index';
import { FaCheck, FaXmark, FaHandshake, FaPercent, FaShieldHalved } from 'react-icons/fa6';
import { auth } from '@/services/auth';
import { constant } from '@/helpers/constant';

function Partnership({ categories, products }) {
  const [body, setBody] = useState({
    name: '',
    email: '',
    mobile: '',
    message: '',
    turnstile: '',
  });

  useEffect(() => {
    const user = auth.store.logged.get();
    if (user) {
      setBody((prev) => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        mobile: user.mobile || '',
      }));
    }
  }, []);

  const handleInputChange = ({ target }) => {
    setBody({ ...body, [target.name]: target.value });
  };

  async function onSubmit(e) {
    e.preventDefault();
    messages.clear();
    if (!body.mobile || body.mobile.replaceAll(/[()-\s]/g, '').length < 11) {
      messages.error('Por favor, informe um Telefone Celular válido.', false, true);
      e.currentTarget.mobile.focus();
      return;
    }
    if (!body.email || !validate.email(body.email)) {
      messages.error('Por favor, informe um E-mail válido.', false, true);
      e.currentTarget.email.focus();
      return;
    }
    layout.loading(true);
    http.post('/partner', body, (result) => {
      setBody({ ...body, turnstile: '' });
      layout.loading(false);
      if (result) {
        messages.success('Agradecemos seu interesse. Em breve entraremos em contato.', false, true);
        setBody({
          name: '',
          email: '',
          mobile: '',
          message: '',
          turnstile: '',
        });
        return;
      }
      messages.error('Erro ao enviar mensagem! Por favor, tente novamente dentro de alguns minutos.', false, true);
    });
  }

  const productDiscount = (code, discount) => {
    const defaultValues = {
      LEILAOESPECIAL: 34.90,
      COMPLETA: 54.90,
      ESPECIAL: 44.90,
      VEICULOTOTALPLUS: 79.90,
    };

    let basePrice = defaultValues[code] || 49.90;

    if (Array.isArray(products) && products.length > 0) {
      const p = products.find((el) => el?.code === code);
      if (p && p.value > 0) {
        basePrice = p.value;
      }
    } else if (categories) {
      const allCat = Object.values(categories).flat();
      const p = allCat.find((el) => el?.code === code);
      if (p && p.value > 0) {
        basePrice = p.value;
      }
    }

    const finalVal = basePrice - (basePrice * discount);
    return format.currency(finalVal);
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://aboutcar.com.br/'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Parceria',
        item: 'https://aboutcar.com.br/parceria'
      }
    ]
  };

  return (
    <div className="partner">
      <Seo
        title="Parceria e Revenda de Consulta Veicular | AboutCar"
        description="Seja um parceiro AboutCar e integre nossas soluções de consulta de histórico veicular por placa na sua empresa, loja de carros ou sistema via API."
        keywords="parceria consulta veicular, revenda consulta placa, api consulta veicular, laudo veicular empresa, parceria seminovos"
        schema={breadcrumbSchema}
      />

      {/* Hero Section */}
      <section className="bg-light-subtle border-bottom py-5">
        <Container className="py-2">
          <Row className="align-items-center g-4">
            <Col lg={7}>
              <span className="badge bg-primary text-white rounded-pill px-3 py-1 text-uppercase fw-bold mb-3 fs-7">AboutCar + Sua Empresa</span>
              <h1 className="display-4 fw-extrabold text-dark mb-3">
                O primeiro passo para ter <span className="text-primary">sucesso nas vendas</span>
              </h1>
              <p className="lead text-secondary mb-4">
                Segurança e tranquilidade de verdade. Ofereça consultas veiculares completas e agregue valor ao seu negócio.
              </p>

              <Row className="g-3">
                <Col sm={6}>
                  <div className="p-3 bg-white rounded-4 shadow-sm border border-light-subtle d-flex align-items-center gap-3">
                    <div className="bg-primary-subtle text-primary p-2 rounded-3">
                      <FaPercent className="fs-4" />
                    </div>
                    <div>
                      <span className="d-block text-muted small fw-semibold">Descontos Exclusivos</span>
                      <strong className="text-dark small">Até 25% de margem</strong>
                    </div>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="p-3 bg-white rounded-4 shadow-sm border border-light-subtle d-flex align-items-center gap-3">
                    <div className="bg-success-subtle text-success p-2 rounded-3">
                      <FaShieldHalved className="fs-4" />
                    </div>
                    <div>
                      <span className="d-block text-muted small fw-semibold">Disponibilidade</span>
                      <strong className="text-dark small">Plataforma Online 24/7</strong>
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>

            <Col lg={5} className="text-center">
              <img src="/assets/images/partner.svg" className="img-fluid max-h-350" border="0" alt="Parceria" loading="lazy" />
            </Col>
          </Row>
        </Container>
      </section>

      {/* Main Content */}
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col lg={10} xl={9}>

            {/* Table 1: Benefícios */}
            <Card className="border-0 shadow-sm rounded-4 overflow-hidden mb-5">
              <Card.Header className="bg-transparent border-0 pt-4 px-4 pb-0 text-center">
                <h3 className="fw-bold text-dark mb-1">Por que ser um Parceiro AboutCar?</h3>
                <p className="text-secondary small mb-0">Compare os diferenciais da nossa plataforma em relação ao mercado</p>
              </Card.Header>
              <Card.Body className="p-4">
                <div className="table-responsive">
                  <Table hover align-middle className="text-center mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="text-start p-3 text-secondary">Benefício</th>
                        <th className="p-3">
                          <span className="badge bg-primary text-white rounded-pill px-3 py-1 fw-bold fs-7">AboutCar</span>
                        </th>
                        <th className="p-3 text-muted">Outros</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <th className="text-start p-3 fw-semibold text-dark">Disponibilidade 24/7</th>
                        <td className="p-3 text-success fs-5"><FaCheck /></td>
                        <td className="p-3 text-danger fs-5 opacity-50"><FaXmark /></td>
                      </tr>
                      <tr>
                        <th className="text-start p-3 fw-semibold text-dark">Até 25% de desconto progressivo</th>
                        <td className="p-3 text-success fs-5"><FaCheck /></td>
                        <td className="p-3 text-danger fs-5 opacity-50"><FaXmark /></td>
                      </tr>
                      <tr>
                        <th className="text-start p-3 fw-semibold text-dark">Consultas simplificadas via WhatsApp</th>
                        <td className="p-3 text-success fs-5"><FaCheck /></td>
                        <td className="p-3 text-danger fs-5 opacity-50"><FaXmark /></td>
                      </tr>
                      <tr>
                        <th className="text-start p-3 fw-semibold text-dark">Liberação e retorno em segundos</th>
                        <td className="p-3 text-success fs-5"><FaCheck /></td>
                        <td className="p-3 text-danger fs-5 opacity-50"><FaXmark /></td>
                      </tr>
                      <tr>
                        <th className="text-start p-3 fw-semibold text-dark">Suporte técnico especializado dedicado</th>
                        <td className="p-3 text-success fs-5"><FaCheck /></td>
                        <td className="p-3 text-danger fs-5 opacity-50"><FaXmark /></td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>

            {/* Table 2: Exemplos de Desconto */}
            <Card className="border-0 shadow-sm rounded-4 overflow-hidden mb-5">
              <Card.Header className="bg-transparent border-0 pt-4 px-4 pb-0 text-center">
                <h3 className="fw-bold text-dark mb-1">Exemplos de Desconto Progressivo</h3>
                <p className="text-secondary small mb-0">Confira a economia estimada de acordo com a sua faixa de desconto</p>
              </Card.Header>
              <Card.Body className="p-4">
                <div className="table-responsive">
                  <Table bordered hover align-middle className="text-center mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="text-start p-3 text-secondary">Consulta</th>
                        <th className="p-3 text-primary fw-bold">10% OFF</th>
                        <th className="p-3 text-primary fw-bold">15% OFF</th>
                        <th className="p-3 text-primary fw-bold">20% OFF</th>
                        <th className="p-3 text-primary fw-bold">25% OFF</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <th className="text-start p-3 fw-bold text-dark">Leilão Especial</th>
                        <td className="p-3 text-secondary fw-semibold">{productDiscount('LEILAOESPECIAL', 0.1)}</td>
                        <td className="p-3 text-secondary fw-semibold">{productDiscount('LEILAOESPECIAL', 0.15)}</td>
                        <td className="p-3 text-secondary fw-semibold">{productDiscount('LEILAOESPECIAL', 0.2)}</td>
                        <td className="p-3 text-primary fw-bold bg-primary-subtle">{productDiscount('LEILAOESPECIAL', 0.25)}</td>
                      </tr>
                      <tr>
                        <th className="text-start p-3 fw-bold text-dark">Completa</th>
                        <td className="p-3 text-secondary fw-semibold">{productDiscount('COMPLETA', 0.1)}</td>
                        <td className="p-3 text-secondary fw-semibold">{productDiscount('COMPLETA', 0.15)}</td>
                        <td className="p-3 text-secondary fw-semibold">{productDiscount('COMPLETA', 0.2)}</td>
                        <td className="p-3 text-primary fw-bold bg-primary-subtle">{productDiscount('COMPLETA', 0.25)}</td>
                      </tr>
                      <tr>
                        <th className="text-start p-3 fw-bold text-dark">Especial</th>
                        <td className="p-3 text-secondary fw-semibold">{productDiscount('ESPECIAL', 0.1)}</td>
                        <td className="p-3 text-secondary fw-semibold">{productDiscount('ESPECIAL', 0.15)}</td>
                        <td className="p-3 text-secondary fw-semibold">{productDiscount('ESPECIAL', 0.2)}</td>
                        <td className="p-3 text-primary fw-bold bg-primary-subtle">{productDiscount('ESPECIAL', 0.25)}</td>
                      </tr>
                      <tr>
                        <th className="text-start p-3 fw-bold text-dark">Veículo Total Plus</th>
                        <td className="p-3 text-secondary fw-semibold">{productDiscount('VEICULOTOTALPLUS', 0.1)}</td>
                        <td className="p-3 text-secondary fw-semibold">{productDiscount('VEICULOTOTALPLUS', 0.15)}</td>
                        <td className="p-3 text-secondary fw-semibold">{productDiscount('VEICULOTOTALPLUS', 0.2)}</td>
                        <td className="p-3 text-primary fw-bold bg-primary-subtle">{productDiscount('VEICULOTOTALPLUS', 0.25)}</td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>

            {/* Form Section */}
            <Card className="border-0 shadow-sm rounded-4 p-4 p-md-5">
              <div className="text-center mb-4">
                <span className="badge bg-primary text-white rounded-pill px-3 py-1 text-uppercase fw-bold mb-2 small">CADASTRO DE INTERESSE</span>
                <h2 className="display-6 fw-bold text-dark mb-2">Seja um Parceiro Oficial</h2>
                <p className="text-secondary small">Preencha seus dados abaixo e nossa equipe comercial entrará em contato em instantes.</p>
              </div>

              <Form onSubmit={onSubmit}>
                <Row className="g-3">
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label htmlFor="name" className="fw-semibold text-secondary small mb-1">Seu Nome / Razão Social</Form.Label>
                      <Form.Control
                        type="text"
                        id="name"
                        name="name"
                        placeholder="Digite seu nome completo ou da empresa"
                        required
                        className="form-control-lg rounded-3 py-2 px-3 shadow-sm border-light-subtle"
                        value={body.name}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col xs={12} sm={7}>
                    <Form.Group>
                      <Form.Label htmlFor="email" className="fw-semibold text-secondary small mb-1">E-mail Comercial</Form.Label>
                      <Form.Control
                        type="email"
                        id="email"
                        name="email"
                        placeholder="seuemail@empresa.com.br"
                        required
                        className="form-control-lg rounded-3 py-2 px-3 shadow-sm border-light-subtle"
                        value={body.email}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col xs={12} sm={5}>
                    <Form.Group>
                      <Form.Label htmlFor="mobile" className="fw-semibold text-secondary small mb-1">Telefone WhatsApp</Form.Label>
                      <InputMask
                        id="mobile"
                        name="mobile"
                        className="form-control form-control-lg rounded-3 py-2 px-3 shadow-sm border-light-subtle"
                        mask="(99) 99999-9999"
                        slotChar=""
                        required
                        value={body.mobile}
                        onChange={(e) => setBody({ ...body, mobile: e.value })}
                        placeholder="(00) 00000-0000"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="text-center mt-4 pt-2">
                  <Button type="submit" size="lg" className="btn-gradient-primary rounded-pill px-5 py-3 fw-bold border-0 shadow fs-5 w-100 w-md-auto" disabled={!body.turnstile}>
                    {body.turnstile ? (
                      <>
                        <FaHandshake className="me-2" />
                        Quero Ser Parceiro
                      </>
                    ) : (
                      <span>Validando acesso...</span>
                    )}
                  </Button>
                </div>
              </Form>

              <Turnstile sitekey={constant.turnstile.siteKey} onVerify={(token) => { setBody({ ...body, turnstile: token }); }} />
            </Card>

          </Col>
        </Row>
      </Container>
    </div>
  );
}

Partnership.enableMessages = true;
export default Partnership;

export async function getServerSideProps(context) {
  try {
    const res = await http.baseAsync('GET', '/products', null, false, auth.headers());
    const products = await res.json();
    if (!products) {
      return { props: { products: [], categories: {} } };
    }
    const categories = {};
    if (Array.isArray(products)) {
      products.forEach((item) => {
        if (item.testDrive || item.trial) {
          return;
        }
        if (!categories[item.category]) {
          categories[item.category] = [];
        }
        categories[item.category].push(item);
      });
    }
    return { props: { products: Array.isArray(products) ? products : [], categories } };
  } catch (e) {
    return { props: { products: [], categories: {} } };
  }
}
